import { Button, ErrorMessage, PageContainer, Spinner } from "@/components";
import { cn } from "@/lib/utils";
import { useApplyMutation } from "@/hooks/api/application.queries";
import { useBookmarkToggle } from "@/hooks/api/bookmark.queries";
import { useInternshipByIdQuery, useCompanyProfileQuery } from "@/hooks/api/internship.queries";
import { useResumesQuery } from "@/hooks/api/profile.queries";
import {
  ArrowLeft,
  Bookmark,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  GraduationCap,
  IndianRupee,
  Loader2,
  Share2,
  Sparkles,
  Users,
  Wifi,
} from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function DiscoverInternshipView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: job, isLoading, isError, refetch } = useInternshipByIdQuery(id || "");
  const { data: companyProfile } = useCompanyProfileQuery(job?.recruiter_id || "", !!job?.recruiter_id);
  const { data: resumes = [] } = useResumesQuery();
  const { isBookmarked, toggle: toggleBookmark, isPending: bookmarkPending } = useBookmarkToggle(id || "");
  const applyMutation = useApplyMutation();

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    import("@/lib/notification").then(({ notifySuccess }) => {
      notifySuccess("Link copied to clipboard! 🔗");
    });
  };

  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedResumeId, setSelectedResumeId] = useState<string>("");
  const [coverLetter, setCoverLetter] = useState("");

  const primaryResume = resumes.find((r: any) => r.is_primary) || resumes[0];

  const handleApply = async () => {
    if (!id) return;
    await applyMutation.mutateAsync({
      internship_id: id,
      resume_id: selectedResumeId || primaryResume?.id || null,
      cover_letter: coverLetter || null,
    });
    setApplyDialogOpen(false);
    setCoverLetter("");
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Spinner />
        <p className="text-gray-400 text-sm animate-pulse">Loading internship details...</p>
      </div>
    );
  }
  // null = the posting resolved but no longer exists — retrying won't help.
  if (job === null) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-5 text-center">
          <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center">
            <Briefcase size={32} className="text-slate-300" />
          </div>
          <div className="space-y-1 max-w-sm">
            <h2 className="text-xl font-bold text-slate-700">
              This position is no longer available
            </h2>
            <p className="text-sm text-slate-400">
              The recruiter has removed this posting. It may have been filled or
              closed.
            </p>
          </div>
          <Button
            onClick={() => navigate("/discover/internships")}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
          >
            <ArrowLeft size={16} className="mr-1" /> Browse Internships
          </Button>
        </div>
      </PageContainer>
    );
  }

  if (isError || !job) {
    return <ErrorMessage message="Failed to load internship details" onRetry={() => refetch()} />;
  }

  const title = job.title || job.position || "Untitled Position";
  const companyName = job.company_name || job.company || "Confidential Company";
  const location = job.location || (job.is_remote ? "Remote" : "Any Location");
  const stipend = job.stipend_min
    ? `₹${job.stipend_min.toLocaleString()}${job.stipend_max ? `–${job.stipend_max.toLocaleString()}` : ""} /mo`
    : "Unpaid / Depends";

  return (
    <PageContainer>
      {/* Back nav */}
      <button
        onClick={() => navigate(-1)}
        className="group flex items-center gap-2 text-sm text-gray-500 hover:text-indigo-600 transition mb-6"
      >
        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
        Back to Listings
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ─── Left / Main ─── */}
        <div className="lg:col-span-2 space-y-6">
          {/* Hero Card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-indigo-500 via-violet-500 to-pink-500" />
            <div className="p-8">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                <div className="flex items-start gap-4">
                  <div
                    className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center shrink-0 overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                    onClick={() => job?.recruiter_id && navigate(`/discover/companies/${job.recruiter_id}`)}
                  >
                    {companyProfile?.logo_url ? (
                      <img src={companyProfile.logo_url} alt={companyName} className="w-full h-full object-contain bg-white p-1" />
                    ) : (
                      <Building2 size={28} className="text-indigo-600" />
                    )}
                  </div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-gray-900 leading-tight mb-1">{title}</h1>
                    <p
                      className="text-indigo-600 font-semibold text-base cursor-pointer hover:underline"
                      onClick={() => job?.recruiter_id && navigate(`/discover/companies/${job.recruiter_id}`)}
                    >
                      {companyName}
                    </p>
                  </div>
                </div>
                {job.status === "open" && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-widest rounded-full border border-emerald-100 shrink-0 self-start">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                    Accepting Applications
                  </span>
                )}
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-5 border-y border-gray-100">
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Location</span>
                  <span className="font-bold text-gray-800 text-sm flex items-center gap-1.5">
                    <Wifi size={13} className="text-gray-400" />{location}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Domain</span>
                  <span className="font-bold text-gray-800 text-sm capitalize">{job.domain || job.type || "General"}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Duration</span>
                  <span className="font-bold text-gray-800 text-sm flex items-center gap-1.5">
                    <Clock size={13} className="text-gray-400" />
                    {job.duration_weeks ? `${job.duration_weeks} Weeks` : (job.duration || "Flexible")}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Stipend</span>
                  <span className="font-bold text-emerald-600 text-sm flex items-center gap-1">
                    <IndianRupee size={13} />{stipend.replace("₹", "")}
                  </span>
                </div>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mt-5">
                {job.is_remote && (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full border border-blue-100">
                    <Wifi size={12} /> Remote
                  </span>
                )}
                {job.openings && (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 text-xs font-semibold rounded-full border border-amber-100">
                    <Users size={12} /> {job.openings} Opening{job.openings > 1 ? "s" : ""}
                  </span>
                )}
                {job.application_deadline && (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 text-xs font-semibold rounded-full border border-red-100">
                    <Calendar size={12} /> Deadline: {new Date(job.application_deadline).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Description Card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
            <div className="flex items-center gap-2 mb-5">
              <Sparkles size={18} className="text-indigo-500" />
              <h2 className="text-xl font-bold text-gray-900">About the Role</h2>
            </div>
            <div
              className="prose prose-sm text-gray-600 max-w-none leading-relaxed whitespace-pre-wrap"
            >
              {job.description || "No description provided."}
            </div>
          </div>

          {/* Requirements Card */}
          {job.requirements && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-5">Requirements</h2>
              <div
                className="prose prose-sm text-gray-600 max-w-none bg-gray-50/70 p-6 rounded-xl border border-gray-100"
                dangerouslySetInnerHTML={{ __html: job.requirements }}
              />
            </div>
          )}
        </div>

        {/* ─── Right / Actions ─── */}
        <div className="space-y-5">
          {/* Apply Card */}
          <div className="bg-gradient-to-br from-indigo-600 to-violet-700 rounded-2xl p-6 text-white shadow-lg">
            <h3 className="text-lg font-bold mb-1">Ready to apply?</h3>
            <p className="text-indigo-200 text-sm mb-6">Make sure your primary resume is updated before applying.</p>
            <Button
              onClick={() => setApplyDialogOpen(true)}
              className="w-full bg-white text-indigo-700 hover:bg-indigo-50 font-bold py-5 text-base rounded-xl shadow-md group mb-3 transition-all"
            >
              Apply Now <ChevronRight size={18} className="ml-1 group-hover:translate-x-1 transition-transform" />
            </Button>
            <div className="flex gap-3 mt-2">
              <button
                onClick={toggleBookmark}
                disabled={bookmarkPending}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-all duration-300 text-sm font-bold",
                  isBookmarked
                    ? "bg-amber-400 border-amber-400 text-indigo-950 shadow-lg shadow-amber-400/20"
                    : "bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30"
                )}
              >
                {bookmarkPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Bookmark size={16} className={isBookmarked ? "fill-current" : ""} />
                )}
                {isBookmarked ? "Saved in Bookmarks" : "Save Choice"}
              </button>
              <button
                onClick={handleShare}
                className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/10 border-2 border-white/20 text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300"
                title="Share link"
              >
                <Share2 size={18} />
              </button>
            </div>
          </div>

          {/* Skills & Eligibility */}
          {(job.required_skills?.length > 0 || job.target_degree_types?.length > 0) && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
              {job.target_degree_types?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2 text-sm">
                    <GraduationCap size={16} className="text-indigo-500" /> Who Can Apply
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {job.target_degree_types.map((deg: string, i: number) => (
                      <span key={i} className="px-3 py-1.5 bg-amber-50 text-amber-800 border border-amber-100 text-xs font-medium rounded-full uppercase">
                        {deg}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {job.required_skills?.length > 0 && (
                <div className={job.target_degree_types?.length > 0 ? "pt-4 border-t border-gray-100" : ""}>
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2 text-sm">
                    <CheckCircle2 size={16} className="text-indigo-500" /> Required Skills
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {job.required_skills.map((skill: string, i: number) => (
                      <span key={i} className="px-3 py-1.5 bg-gray-50 text-gray-700 border border-gray-200 text-xs font-medium rounded-full hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-100 transition">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Start Date */}
          {job.start_date && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
              <div className="w-12 h-12 bg-rose-50 rounded-xl flex items-center justify-center shrink-0">
                <Calendar size={20} className="text-rose-500" />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Start Date</p>
                <p className="font-bold text-gray-900">
                  {new Date(job.start_date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Apply Dialog ─── */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Briefcase size={18} className="text-indigo-600" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-gray-900">Apply for this Role</DialogTitle>
                <DialogDescription className="text-sm text-gray-500">{title} · {companyName}</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Resume Selector */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700">Select Resume</Label>
              {resumes.length === 0 ? (
                <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4 text-sm text-amber-700 flex items-center gap-2">
                  <span>⚠️</span>
                  No resumes found. Please upload one in your profile first.
                </div>
              ) : (
                <div className="space-y-2">
                  {resumes.map((resume: any) => (
                    <label
                      key={resume.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${(selectedResumeId || primaryResume?.id) === resume.id
                          ? "border-indigo-300 bg-indigo-50"
                          : "border-gray-200 hover:border-gray-300"
                        }`}
                    >
                      <input
                        type="radio"
                        name="resume"
                        value={resume.id}
                        checked={(selectedResumeId || primaryResume?.id) === resume.id}
                        onChange={() => setSelectedResumeId(resume.id)}
                        className="accent-indigo-600"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          Resume v{resume.version}
                          {resume.is_primary && (
                            <span className="ml-2 text-[10px] font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded uppercase tracking-wider">
                              Primary
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Uploaded {new Date(resume.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Cover Letter */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700">
                Cover Letter <span className="text-gray-400 font-normal">(optional)</span>
              </Label>
              <Textarea
                placeholder="Tell the recruiter why you're a great fit for this role..."
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                className="min-h-[120px] resize-none rounded-xl border-gray-200 focus:border-indigo-400 focus:ring-indigo-100 text-sm"
              />
              <p className="text-xs text-gray-400">{coverLetter.length}/2000 characters</p>
            </div>
          </div>

          <DialogFooter className="gap-2 flex-row pt-2">
            <Button
              variant="outline"
              onClick={() => setApplyDialogOpen(false)}
              className="flex-1 rounded-xl border-gray-200"
              disabled={applyMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold"
              disabled={applyMutation.isPending || resumes.length === 0}
            >
              {applyMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" /> Submitting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CheckCircle2 size={16} /> Submit Application
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
